import { DateTime } from 'luxon'
import { action, computed, makeObservable, observable } from 'mobx'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Cubit } from '../core'
import { type InstructorTeachingPlanCubit } from '../InstructorTeachingPlanCubit'
import { SectionSelectionCubit } from '../SectionSelectionCubit'
import { FormCubit } from '../shared/FormCubit'
import { AssignNextbookProcessingController } from './controllers/ProcessingController'
import { AssignNextbookScheduleController } from './controllers/ScheduleController'
import { AssignNextbookStorageController } from './controllers/StorageController'
import { AssignNextbookValidationController } from './controllers/ValidationController'
import {
  AssignNextbookCompletionDurations,
  AssignNextbookFrequencies,
  AssignNextbookSteps,
  AssignNextbookSubSteps,
} from './schemas'
import type {
  AssignNextbookErrors,
  AssignNextbookFields,
  AssignNextbookLtiOutput,
  StorageProvider,
} from './types'
import type { BreakoutUser } from '../../models/BreakoutUser'
import type z from 'zod'

export class AssignNextbookCubit extends Cubit {
  repository: FirebaseRepository
  teachingPlanCubit: InstructorTeachingPlanCubit

  closeCallback?: () => void

  @observable
  currentStep: number = 0

  form: FormCubit<AssignNextbookFields, AssignNextbookErrors>

  storage: AssignNextbookStorageController
  validator: AssignNextbookValidationController
  schedule: AssignNextbookScheduleController
  processing: AssignNextbookProcessingController

  sectionSelectionCubit: SectionSelectionCubit

  ltiModeOn: boolean
  ltiOptions: { organizationId?: string } | undefined
  ltiCallback?: (inputs: AssignNextbookLtiOutput[]) => Promise<void>

  constructor(
    repository: FirebaseRepository,
    impersonatedBreakoutUser: BreakoutUser,
    options: {
      ltiModeOn?: boolean
      ltiOptions?: { organizationId?: string } | null
      ltiCallback?: (inputs: AssignNextbookLtiOutput[]) => Promise<void>
      teachingPlanCubit: InstructorTeachingPlanCubit
      closeCallback?: () => void
      storage?: StorageProvider
    }
  ) {
    super()
    makeObservable(this)

    this.repository = repository
    this.teachingPlanCubit = options.teachingPlanCubit
    this.closeCallback = options.closeCallback
    this.ltiModeOn = options.ltiModeOn ?? false
    this.ltiOptions = options.ltiOptions ?? undefined
    this.ltiCallback = options.ltiCallback

    this.form = new FormCubit<AssignNextbookFields, AssignNextbookErrors>(
      this.repository
    )

    this.storage = new AssignNextbookStorageController(this, options.storage)
    this.validator = new AssignNextbookValidationController(this)
    this.schedule = new AssignNextbookScheduleController(this)
    this.processing = new AssignNextbookProcessingController(this)

    this.sectionSelectionCubit = new SectionSelectionCubit(
      this.repository,
      impersonatedBreakoutUser,
      {
        catalogId: this.teachingPlanCubit.catalogId,
      }
    )
  }

  initialize(): void {
    this.storage.load()

    this.sectionSelectionCubit.initialize()
    this.schedule.initialize()
    this.storage.initialize()

    this.form.setField('sectionId', this.teachingPlanCubit.sectionId)
  }

  dispose(): symbol {
    this.sectionSelectionCubit.dispose()
    this.schedule.dispose()
    this.storage.dispose()
    return super.dispose()
  }

  get steps() {
    return AssignNextbookSteps.options
  }

  /**
   * Steps to show in the header. Excludes sub-steps.
   */
  get headerSteps() {
    return AssignNextbookSteps.options.filter((step) => {
      const isSubStep = (step: string) =>
        (AssignNextbookSubSteps.options as string[]).includes(step)

      return !isSubStep(step)
    })
  }

  /**
   * Maps currentStep to the corresponding header step index.
   * if currentStep is a sub-step, backtracks to find the parent step index.
   */
  @computed
  get headerStepIndex() {
    const isSubStep = (step: string) =>
      (AssignNextbookSubSteps.options as string[]).includes(step)

    let index = this.currentStep
    let step = this.steps[this.currentStep]

    // Backtrack to find the nearest non-sub-step
    while (index > 0 && isSubStep(step)) {
      index--
      step = this.steps[index]
    }

    return index
  }

  get completionDurations() {
    return AssignNextbookCompletionDurations.options
  }

  @computed
  get isFirstStartDateBeforeSectionStart() {
    const minDate = this.sectionStartDateTime ?? DateTime.now()

    const firstDueDate = this.form.fields.get('firstDueDate')
    if (!firstDueDate) return false

    const timeToComplete = this.form.fields.get('timeToComplete')
    if (!timeToComplete) return false

    const startTime = this.schedule.getStartTime(firstDueDate)

    return startTime < minDate
  }

  @computed
  get isLastDueDateAfterSectionEnd() {
    if (!this.sectionEndDateTime) return false

    const firstDueDate = this.form.fields.get('firstDueDate')
    if (!firstDueDate) return false

    const frequency = this.form.fields.get('frequency')
    if (!frequency) return false

    const slideDeckCount = this.teachingPlanCubit.allSlideDecksPacks.length - 1
    if (slideDeckCount < 0) return false

    let lastDueDate = firstDueDate.plus({ minute: 0 })
    for (let i = 0; i < slideDeckCount; i++) {
      switch (frequency) {
        case 'weekly':
          lastDueDate = lastDueDate.plus({ weeks: 1 })
          break
        case 'biweekly':
          lastDueDate = lastDueDate.plus({ weeks: 2 })
          break
        case 'monthly':
          lastDueDate = lastDueDate.plus({ months: 1 })
          break
      }
    }

    return lastDueDate > this.sectionEndDateTime
  }

  get frequencies() {
    return AssignNextbookFrequencies.options
  }

  @computed
  get step() {
    return AssignNextbookSteps.options[this.currentStep]
  }

  @computed
  get isFirstStep() {
    return this.currentStep === 0
  }

  @computed
  get isLastStep() {
    return this.currentStep === AssignNextbookSteps.options.length - 1
  }

  @computed
  get sectionEndDateTime() {
    return this.section?.sectionEndDateTime
  }

  @computed
  get sectionStartDateTime() {
    return this.section?.sectionStartDateTime
  }

  @computed
  get section() {
    const sectionId = this.form.fields.get('sectionId')
    if (!sectionId) return undefined
    const section = this.sectionSelectionCubit.sections.value?.find(
      (s) => s.id === sectionId
    )

    return section
  }

  @computed
  get isPeerReviewsEnabled() {
    const peerReviewOptions = this.form.fields.get('peerReviewOptions')
    return peerReviewOptions?.peerReviewsEnabled ?? false
  }

  @action
  nextStep() {
    if (!this.validator.validate()) return

    this.storage.save()

    if (this.isLastStep) {
      this.processing.process()
      return
    }

    let nextStepIndex = this.currentStep + 1
    let step = AssignNextbookSteps.options[nextStepIndex]

    // check if we should skip a step
    while (
      this.shouldSkipStep(step) &&
      nextStepIndex < AssignNextbookSteps.options.length
    ) {
      nextStepIndex++
      step = AssignNextbookSteps.options[nextStepIndex]
    }

    this.currentStep = Math.min(
      nextStepIndex,
      AssignNextbookSteps.options.length - 1
    )
  }

  @action
  shouldSkipStep(step: z.infer<typeof AssignNextbookSteps>) {
    switch (step) {
      case 'configure_peer_reviews':
        return !this.isPeerReviewsEnabled
      // only skip configure for roles step IF the input value in configure_grouping
      // step doesn't have any collisions with role specific constraints
      case 'configure_grouping_roles': {
        const assignments = this.form.fields.get('assignments') ?? []

        if (assignments.some((a) => a.groupSizeOverride !== undefined))
          return false

        const errors = {}
        this.validator.validateConfirmGroupingRoles(errors)
        const hasErrors = Object.keys(errors).length > 0
        return !hasErrors
      }
      default:
        return false
    }
  }

  @action
  previousStep() {
    let prevStepIndex = this.currentStep - 1
    let step = AssignNextbookSteps.options[prevStepIndex]

    // check if we should skip a step
    while (this.shouldSkipStep(step) && prevStepIndex >= 0) {
      prevStepIndex--
      step = AssignNextbookSteps.options[prevStepIndex]
    }

    this.currentStep = Math.max(prevStepIndex, 0)
  }

  close() {
    this.closeCallback?.()
  }
}
